import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { Grade, GradeHistory } from '../../../../back-end/utilities/apiDefinitions'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { PageStatus } from '../../constants/pageStatus'
import { Loading } from '../ui/Loading/Loading'

import './gradeSettings.css'
import { DateTime } from 'luxon'
import AddEditGradeModal from './addEditGradeModal'
import { formatDateExcludeEpoch } from '../../utilities/format'
import { OnboardingStep } from '../../screens/onBoarding/onBoarding'
import { useNavigate } from 'react-router-dom'
import { MessageAction } from '../ui/messages/message'

interface BillyGradeSettingsCardProps {
	updateMessages: (action: MessageAction) => void
}

const defaultGrade: Partial<Omit<Grade, 'gradeHistory'>> & { gradeHistory: Partial<GradeHistory>[] } = {
	name: '',
	isActive: true,
	gradeHistory: [
		{
			rate: 0,
			costRate: 0,
			effectiveDate: DateTime.now().plus({ days: 1 }).toISODate(),
		},
	],
}

const GradeSettings = (props: BillyGradeSettingsCardProps) => {
	const context = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')
	const navigate = useNavigate()
	const [grades, setGrades] = React.useState<Grade[]>([])
	const [modalGrade, setModalGrade] = React.useState<Grade | null>(null)

	React.useEffect(() => {
		const getData = async () => {
			const [gradesRes] = await Promise.all([Request.get('grade')])

			setGrades(gradesRes.data.grades)

			setPageStatus('Ready')
		}

		setPageStatus('Loading')
		if (context.appState.authState.isLoggedIn) {
			getData()
		}
	}, [context.appState])

	React.useEffect(() => {
		// Case 1: Still on GradeSettings and no grades yet
		if (context.appState.onBoardingStep === OnboardingStep.GradeSettings && grades.length === 0) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'info',
					message: 'This step is required to ensure that time input to Billy contains the correct sell rates and cost rates where defined.',
					timeout: 0,
					dismissible: true,
				},
			})
		}

		// Case 2: Grades exist -> progress onboarding
		if (context.appState.onBoardingStep === OnboardingStep.GradeSettings && grades.length > 0) {
			context.updateAppState({
				state: 'onboarding',
				data: {
					...context.appState,
					onBoardingStep: OnboardingStep.GradeSettings + 1,
				},
			})
			navigate('/onboarding')
		}
	}, [context.appState.onBoardingStep, grades.length])

	if (pageStatus === 'Loading') {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	const costRateIsRequired =
		context.appState.accounts.find((acc) => acc.accountID === context.appState.authState.attributes?.['custom:currentAccountId'])?.billySettings
			?.costRateIsRequired || false

	return (
		<>
			<Row className="align-items-center pb-2">
				<Col sm="auto">
					<h3 className="mb-0">Grades</h3>
				</Col>
				<Col />
				<Col sm="auto">
					<Button variant="primary" onClick={() => setModalGrade(defaultGrade as Grade)}>
						New Grade
					</Button>
				</Col>
			</Row>
			<Row className="grade-list-header align-items-center py-3">
				<Col sm={costRateIsRequired ? 3 : 4}>Grade</Col>
				{costRateIsRequired ? <Col sm={2}>Cost Rate/Hr</Col> : null}
				<Col sm={costRateIsRequired ? 2 : 3}>Sell Rate/Hr</Col>
				<Col sm={3}>Effective Date</Col>
				<Col sm={2}></Col>
			</Row>
			{grades
				.sort((a, b) => (a.name < b.name ? -1 : 1))
				.map((grade) => {
					const newestGradeHistory = grade.gradeHistory.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1))[0]
					return (
						<Row key={grade.gradeID} className="align-items-center pt-3">
							<Col sm={costRateIsRequired ? 3 : 4}>{grade.name}</Col>
							{costRateIsRequired ? <Col sm={2}>{newestGradeHistory.costRate ? `$${newestGradeHistory.costRate}` : ''}</Col> : null}
							<Col sm={costRateIsRequired ? 2 : 3}>${newestGradeHistory.rate}</Col>
							<Col sm={3}>{formatDateExcludeEpoch(newestGradeHistory.effectiveDate)}</Col>
							<Col sm={2}>
								<Button variant="secondary" className="p-2" onClick={() => setModalGrade(grade)}>
									Update
								</Button>
							</Col>
						</Row>
					)
				})}

			{modalGrade !== null && (
				<AddEditGradeModal
					authState={context.appState.authState}
					costRateIsRequired={costRateIsRequired}
					modalGrade={modalGrade}
					setModalGrade={setModalGrade}
					grades={grades}
					setGrades={setGrades}
				/>
			)}
		</>
	)
}

export default GradeSettings
